<script setup lang="ts">
/**
 * The centred card every screen someone reaches without a session is drawn in.
 *
 * Shared because two applications draw one: identity, for every step of
 * signing up and in, and the photo app inside its Android shell - which cannot
 * send anybody to identity to sign in, because the session identity would set
 * is a cookie the shell's WebView never receives. Like everything here it does
 * not translate: the brand arrives as a prop with the rest of the copy.
 */
defineProps<{ brand: string; title: string; subtitle?: string | undefined }>();
</script>

<template>
  <main class="hb-auth">
    <div class="hb-auth__card">
      <p class="hb-auth__brand">{{ brand }}</p>
      <h1 class="hb-auth__title">{{ title }}</h1>
      <p v-if="subtitle" class="hb-auth__subtitle">{{ subtitle }}</p>

      <div class="hb-auth__body">
        <slot />
      </div>

      <div v-if="$slots.footer" class="hb-auth__footer">
        <slot name="footer" />
      </div>
    </div>
  </main>
</template>

<style scoped>
.hb-auth {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;

  /* Phones first: the card is the page, edge to edge with breathing room. */
  padding: var(--hb-space-4);
}

.hb-auth__card {
  width: 100%;
  max-width: 26rem;
  display: flex;
  flex-direction: column;
  gap: var(--hb-space-4);
}

@media (min-width: 640px) {
  .hb-auth__card {
    padding: var(--hb-space-6);
    background: var(--hb-surface);
    border: 1px solid var(--hb-border);
    border-radius: var(--hb-radius-lg);
    box-shadow: var(--hb-shadow-1);
  }
}

.hb-auth__brand {
  color: var(--hb-accent);
  font-size: var(--hb-text-sm);
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.hb-auth__title {
  font-size: var(--hb-text-2xl);
  font-weight: 600;
  line-height: 1.2;
}

.hb-auth__subtitle {
  color: var(--hb-text-muted);
  font-size: var(--hb-text-sm);
  margin-top: calc(var(--hb-space-2) * -1);
}

.hb-auth__body {
  display: flex;
  flex-direction: column;
  gap: var(--hb-space-4);
}

.hb-auth__footer {
  border-top: 1px solid var(--hb-border);
  padding-top: var(--hb-space-4);
  color: var(--hb-text-muted);
  font-size: var(--hb-text-sm);
  text-align: center;
}
</style>
